'use client';

import cn from 'classnames';
import { ReactNode } from 'react';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { responsiveBreakpointsPx } from '@/constants';
import { CSSPropsVars } from '@/interfaces/types/CSSPropsVars';
import { colorBrightness } from '@/packages/color/colorBrightness';
import styles from './testimonial.module.scss';

export interface TestimonialProps {
    backgroundColor?: string;
    quote: ReactNode;
    includeQuotationMarks?: boolean;
    authorImage?: string;
    authorImageAlt?: string;
    authorName: string;
    authorSubtitle?: string;
    featured?: boolean;
    excludeResponsiveSizes?: (keyof typeof responsiveBreakpointsPx)[];
}

const HALF_LUMINANCE = 255 / 2;

export function Testimonial({
    backgroundColor,
    quote,
    includeQuotationMarks = false,
    authorImage,
    authorImageAlt = "Author's image",
    authorName,
    authorSubtitle,
    featured,
    excludeResponsiveSizes = [],
}: TestimonialProps): React.JSX.Element {
    const backgroundColorCustomProperty: CSSPropsVars | undefined = backgroundColor
        ? { '--testimonialBackgroundColor': backgroundColor }
        : undefined;
    const backgroundColorBrightness = colorBrightness(backgroundColor ?? '#FFFFFF');
    const excludedResponsiveSizeClassNames = excludeResponsiveSizes.map((sizeName) => {
        const finalSizeName = sizeName == 'xl' || sizeName == 'nav-tablet' ? 'lg' : sizeName;
        return styles[`exclude-${finalSizeName}`];
    });
    const wideLgGridSpan = excludeResponsiveSizes.some((size) => ['lg', 'xl', 'nav-tablet'].includes(size));

    const wrapperClassNames = cn(styles.wrapper, ...excludedResponsiveSizeClassNames, {
        [styles.hasAuthorImage]: !!authorImage,
        [styles.featured]: featured,
        [styles.darkBackground]: backgroundColorBrightness < HALF_LUMINANCE,
    });

    return (
        <div style={backgroundColorCustomProperty} className={wrapperClassNames}>
            <Grid container noPadding className={styles.gridContainer}>
                <Grid start={{ lg: wideLgGridSpan ? 1 : 3 }} col={{ lg: wideLgGridSpan ? 12 : 8 }}>
                    <Text tag="p" variant="text-xlarge" className={styles.quote}>
                        {includeQuotationMarks ? <>&ldquo;{quote}&rdquo;</> : quote}
                    </Text>
                </Grid>
            </Grid>

            <div className={styles.authorDetails}>
                {authorImage && (
                    // image size optimised based on container
                    <ImageWrapper
                        src={authorImage}
                        alt={authorImageAlt}
                        className={styles.authorImage}
                        optimizedWidth={50}
                        optimizedHeight={50}
                    />
                )}

                <div>
                    <Text tag="div" variant="text-medium" className={styles.authorName}>
                        {authorName}
                    </Text>

                    {authorSubtitle && (
                        <Text tag="div" variant="text-medium" className={styles.authorSubtitle}>
                            {authorSubtitle}
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );
}
